import { connectToWebSocket } from "apiClient";

type MessageListener = (event: MessageEvent) => void;

type SharedContactSocket = {
  wrapper: ReturnType<typeof connectToWebSocket>;
  listeners: Set<MessageListener>;
};

const socketsByContactId = new Map<number, SharedContactSocket>();

/**
 * Share one `/ws/contacts/{id}/chat/` connection across Chat and other
 * subscribers (e.g. onboarding checklist), so opening Eloria chat does not
 * open a second socket + token-refresh interval for the same contact.
 */
export function subscribeContactChatSocket(
  contactId: number,
  onMessage: MessageListener
): () => void {
  let entry = socketsByContactId.get(contactId);
  if (!entry) {
    const listeners = new Set<MessageListener>();
    const wrapper = connectToWebSocket(
      `/ws/contacts/${contactId}/chat/`,
      (event) => {
        // Snapshot so a listener that unsubscribes mid-dispatch is safe.
        for (const listener of Array.from(listeners)) {
          listener(event);
        }
      }
    );
    entry = { wrapper, listeners };
    socketsByContactId.set(contactId, entry);
  }

  entry.listeners.add(onMessage);

  return () => {
    const current = socketsByContactId.get(contactId);
    if (!current) {
      return;
    }
    current.listeners.delete(onMessage);
    if (current.listeners.size === 0) {
      current.wrapper.close();
      socketsByContactId.delete(contactId);
    }
  };
}
